// Generic API client wrapper — Phase 1 Plan 05.
//
// Responsibilities:
// - Inject `Authorization: Bearer <token>` from getToken() callback.
// - On 401 — call refresh() callback, retry request ONCE with new token.
// - JSON serialization + error handling (throws ApiError on 4xx/5xx).
//
// Token storage lives в AuthContext; this module only knows callbacks.

export interface ApiClientOptions {
  baseUrl: string;
  getToken: () => string | null;
  refresh: () => Promise<string | null>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public body?: unknown,
  ) {
    super(`API ${status}: ${detail}`);
    this.name = "ApiError";
  }
}

export class ApiClient {
  constructor(private opts: ApiClientOptions) {}

  async fetch<T = unknown>(
    path: string,
    init: RequestInit = {},
    options: { skipAuth?: boolean; retryOn401?: boolean } = {},
  ): Promise<T> {
    const url = path.startsWith("http") ? path : `${this.opts.baseUrl}${path}`;
    const retryOn401 = options.retryOn401 ?? true;

    const buildInit = (token: string | null): RequestInit => {
      const headers = new Headers(init.headers);
      if (!headers.has("Content-Type") && init.body && typeof init.body === "string") {
        headers.set("Content-Type", "application/json");
      }
      headers.set("Accept", "application/json");
      if (!options.skipAuth && token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return { ...init, headers };
    };

    let res = await fetch(url, buildInit(this.opts.getToken()));

    if (res.status === 401 && retryOn401 && !options.skipAuth) {
      // Token expired or invalid — attempt refresh + single retry.
      const newToken = await this.opts.refresh();
      if (newToken) {
        res = await fetch(url, buildInit(newToken));
      }
    }

    if (!res.ok) {
      let detail = res.statusText;
      let body: unknown = undefined;
      try {
        body = await res.json();
        if (body && typeof body === "object" && "detail" in body) {
          detail = String((body as { detail: unknown }).detail);
        }
      } catch {
        // Body not JSON — keep statusText
      }
      throw new ApiError(res.status, detail, body);
    }

    if (res.status === 204) {
      return undefined as T;
    }
    return (await res.json()) as T;
  }

  get<T>(path: string): Promise<T> {
    return this.fetch<T>(path, { method: "GET" });
  }

  post<T>(path: string, body: unknown, options: { skipAuth?: boolean } = {}): Promise<T> {
    return this.fetch<T>(path, { method: "POST", body: JSON.stringify(body) }, options);
  }
}
